import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

/**
 * Hook to manage a selected ID in the URL.
 *
 * Be sure to update `main.tsx` to include your optional param in your route.
 * Like the following:
 *
 * ```
 * {
 *   path: "jobs/:jobId?",
 *   element: <Jobs />,
 * }
 * ```
 */
export const useSelectedIdInUrl = (
  paramName: string,
  options?: { prefixSegment?: string }
): {
  selectedId: string | undefined;
  pushIdToUrl: (id: string) => void;
  setSelectedId: (id: string) => void;
  popIdFromUrl: () => void;
} => {
  const params = useParams<Record<string, string | undefined>>();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedId = useMemo(() => params[paramName], [params, paramName]);

  // Path that selects `id`, truncating out any existing selection and deeper
  // optional ids. Truncating at the current selection (vs. blindly popping N
  // segments) keeps two-optional-id routes like manifest/line-item correct.
  const buildPathWithSelectedId = useCallback(
    (id: string) => {
      const pathSegments = location.pathname.split("/").filter(Boolean);
      if (selectedId) {
        const idx = pathSegments.lastIndexOf(selectedId);
        if (idx >= 0) {
          pathSegments.length = idx;
        }
        if (
          options?.prefixSegment &&
          pathSegments[pathSegments.length - 1] === options.prefixSegment
        ) {
          pathSegments.pop();
        }
      }
      const additions = options?.prefixSegment ? [options.prefixSegment, id] : [id];
      return `/${[...pathSegments, ...additions].join("/")}${location.search}`;
    },
    [location.pathname, location.search, options?.prefixSegment, selectedId]
  );

  const setSelectedId = useCallback(
    (id: string) => {
      navigate(buildPathWithSelectedId(id), { replace: true, preventScrollReset: true });
    },
    [navigate, buildPathWithSelectedId]
  );

  const pushIdToUrl = useCallback(
    (id: string) => {
      // Push the first selection; replace on re-select so Back returns to the
      // list (not previewed rows) and the path never gains a duplicate id.
      navigate(buildPathWithSelectedId(id), {
        replace: !!selectedId,
        preventScrollReset: true,
      });
    },
    [navigate, buildPathWithSelectedId, selectedId]
  );

  const popIdFromUrl = useCallback(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const segmentsToPop = options?.prefixSegment ? 2 : 1;
    for (let i = 0; i < segmentsToPop; i += 1) {
      pathSegments.pop();
    }
    const newPath = `/${pathSegments.join("/")}${location.search}`;
    navigate(newPath, { preventScrollReset: true });
  }, [navigate, location.pathname, location.search, options?.prefixSegment]);

  return {
    selectedId,
    pushIdToUrl,
    setSelectedId,
    popIdFromUrl,
  };
};
